import { UserCode } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, input, signal } from '@angular/core';
import { formatDate, getDate, minutesDiff } from '@app/shared/dateHelper';
import { getCodeStatus } from '../device-code-utils';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Button } from 'primeng/button';

export interface DeviceCodeListActions {
  copyCode: (code: string, event: Event) => void;
  handleCodeAction: (code: UserCode) => void;
}

@Component({
  selector: 'app-device-codes-list',
  imports: [CommonModule, TranslocoModule, Button],
  templateUrl: './device-code-list.component.html',
  standalone: true,
})
export class DeviceCodesListComponent implements OnInit, OnDestroy {
  codes = input<UserCode[]>([]);
  highlightedCodeId = input<string | undefined>(undefined);

  @Output() copyCodeToClipboard = new EventEmitter<{ code: string; event: Event }>();
  @Output() handleCodeAction = new EventEmitter<UserCode>();

  translocoService = inject(TranslocoService);

  private timerInterval: any;
  private currentTime = signal(new Date());

  ngOnInit(): void {
    this.timerInterval = setInterval(() => {
      this.currentTime.set(new Date());
    }, 60000); // Update every minute
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  getCodeStatus(code: UserCode): 'active' | 'used' | 'expired' | 'logged-out' {
    return getCodeStatus(code);
  }

  displayActivatedAtColumn(createdAt: UserCode['createdAt']): string {
    const formattedDate = formatDate(createdAt);
    return formattedDate || this.translocoService.translate('codes.notActivated');
  }

  displayUsedAtColumn(usedAt?: UserCode['usedAt']): string {
    const formattedDate = formatDate(usedAt);
    return formattedDate || '-';
  }

  /**
   * Displays the remaining validity time for an *unused* code based on its creation time.
   * UserCodes are valid for 1 hour after creation if not used.
   * Returns '-' if the code has been used.
   */
  displayExpiresAtColumn(createdAt?: UserCode['createdAt']): string {
    if (!createdAt) return '-';

    const creationTime = getDate(createdAt);
    if (!creationTime) return '-';

    const expiryTime = creationTime ? new Date(creationTime.getTime() + 60 * 60 * 1000) : undefined;
    if (!expiryTime) return '-';

    const now = this.currentTime();
    const expiryTimeIsInThePast = expiryTime < now;
    const minutesRemaining = minutesDiff(now, expiryTime);

    if (expiryTimeIsInThePast || minutesRemaining <= 0) {
      return this.translocoService.translate('codes.expired');
    } else {
      return `${minutesRemaining} min.`;
    }
  }

  getActionButtonLabel(code: UserCode): string {
    const status = this.getCodeStatus(code);

    if (status === 'used') {
      return this.translocoService.translate('codes.logout');
    } else if (status === 'active') {
      return this.translocoService.translate('codes.deactivate');
    } else if (status === 'logged-out') {
      return ''; // No text button for logged-out, only trash icon
    } else {
      return this.translocoService.translate('codes.delete');
    }
  }
}
